import React from "react";
import { Toolbar, makeStyles, Theme, Typography, Button } from "@material-ui/core";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { useTheme } from "@material-ui/core/styles";
import moment from "moment";
import { ADVERT_VALUES } from "consts";

interface Props {
  name: string;
  date: number;
  code: string;
  isFinalScore: boolean;
  widescreen: boolean;
  isLeaderboard: boolean;
  showSwitchButton: boolean;
  titleInDialog?: boolean;
  handleIsLeaderboardChange: (status: boolean) => void;
}

export default function LeaderboardToolbar(props: Props) {
  const classes = useStyles();
  const theme = useTheme();
  const landscape = useMediaQuery(theme.breakpoints.up("md"));

  const {
    name,
    code,
    date,
    isFinalScore,
    widescreen,
    isLeaderboard,
    showSwitchButton,
    handleIsLeaderboardChange,
    titleInDialog = false,
  } = props;

  const switchButton = showSwitchButton ? (
    <Button
      variant="contained"
      color="secondary"
      size="small"
      onClick={() => handleIsLeaderboardChange(!isLeaderboard)}
      className={landscape ? undefined : classes.toggleButton}
    >
      {landscape
        ? isLeaderboard
          ? "View Start Sheet"
          : "View Leaderboard"
        : isLeaderboard
          ? "Start Sheet"
          : "Leaderboard"}
    </Button>
  ) : null;

  const actionControls = switchButton ? <div className={classes.headerActions}>{switchButton}</div> : null;

  const useLargeStatusChip = widescreen && landscape;

  const statusChipClass = `${classes.statusTag} ${isFinalScore ? classes.statusTagFinal : classes.statusTagLive}${
    useLargeStatusChip ? ` ${classes.statusTagWidescreen}` : ""
  }`;

  const renderStatusChip = () => (
    <span className={statusChipClass}>{isFinalScore ? "Final Result" : "Live Score"}</span>
  );

  if (titleInDialog) {
    return (
      <Toolbar
        className={classes.dialogToolbar}
        style={{
          flexWrap: "nowrap",
          marginTop: widescreen && landscape ? ADVERT_VALUES.HEIGHT : 0,
          marginRight: widescreen && landscape ? ADVERT_VALUES.SIDEBAR_WIDTH : 0,
          padding: landscape ? "0.5rem 1rem" : "0.375rem 1rem",
          minHeight: "auto",
        }}
      >
        <div className={classes.dialogMetaBar}>
          <Typography component="p" variant="caption" className={classes.gameCode} noWrap>
            Code: <strong>{code}</strong>
          </Typography>
          <div className={classes.dialogMetaTrailing}>
            {renderStatusChip()}
            {actionControls}
          </div>
        </div>
      </Toolbar>
    );
  }

  return (
    <Toolbar
      style={
        landscape
          ? {
              flexWrap: "wrap",
              marginTop: widescreen ? ADVERT_VALUES.HEIGHT : 0,
              marginRight: widescreen ? ADVERT_VALUES.SIDEBAR_WIDTH : 0,
              padding: "1rem",
              marginBottom: "1rem",
            }
          : {
              flexWrap: "wrap",
              padding: "0.5rem 1rem",
              minHeight: "80px",
            }
      }
    >
      {landscape ? (
        <>
          <div className={classes.landscapeTitleRow}>
            <Typography variant="h1" className={classes.title} style={{ margin: 0, textAlign: "left" }}>
              {name}
              {!widescreen ? (
                <span className={classes.status} style={{ fontSize: "1rem", marginLeft: "10px" }}>
                  ({isFinalScore ? "Final Result" : "Live Score"})
                </span>
              ) : null}
            </Typography>
            {widescreen ? <div className={classes.landscapeStatusWrap}>{renderStatusChip()}</div> : null}
            {actionControls}
          </div>
          <Typography className={classes.landscapeMetaRow}>
            <strong style={{ flex: 1 }}>Code: {code}</strong>
            <strong style={{ flex: 1, textAlign: "center" }}>{moment(date).format("ll")}</strong>
            <strong style={{ flex: 1, textAlign: "right" }}>
              {isLeaderboard ? "Leaderboard View" : "Start Sheet View"}
            </strong>
          </Typography>
        </>
      ) : (
        <>
          <div className={classes.titleRow}>
            <strong className={classes.title}>{name}</strong>
            <span className={classes.statusBadge}>{isFinalScore ? "Final Result" : "Live Score"}</span>
          </div>
          <div className={classes.actionRow}>
            <h3 className={classes.header}>{isLeaderboard ? "Leaderboard" : "Start sheet"}</h3>
            {actionControls}
          </div>
        </>
      )}
    </Toolbar>
  );
}

const useStyles = makeStyles((theme: Theme) => ({
  dialogToolbar: {
    minHeight: "auto !important",
    boxShadow: "inset 0 -1px 0 rgba(226, 232, 240, 0.92)",
    backgroundColor: "#ffffff",
  },
  dialogMetaBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    width: "100%",
    minWidth: 0,
  },
  gameCode: {
    margin: 0,
    flex: "1 1 auto",
    minWidth: 0,
    fontSize: "0.8125rem",
    fontWeight: 400,
    lineHeight: 1.4,
    color: "#64748b",
    letterSpacing: "0.01em",
    "& strong": {
      fontWeight: 600,
      color: "#334155",
    },
  },
  dialogMetaTrailing: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    flexShrink: 0,
    marginLeft: theme.spacing(1),
  },
  statusTag: {
    flexShrink: 0,
    padding: "3px 8px",
    borderRadius: 4,
    fontSize: "0.625rem",
    fontWeight: 700,
    lineHeight: 1.2,
    letterSpacing: "0.05em",
    textTransform: "uppercase",
    whiteSpace: "nowrap",
  },
  statusTagLive: {
    backgroundColor: "#22c55e",
    color: "#ffffff",
  },
  statusTagFinal: {
    backgroundColor: "#64748b",
    color: "#ffffff",
  },
  statusTagWidescreen: {
    [theme.breakpoints.up("md")]: {
      fontSize: "0.875rem",
      padding: "6px 14px",
      borderRadius: 6,
      letterSpacing: "0.06em",
    },
    [theme.breakpoints.up("lg")]: {
      fontSize: "1.125rem",
      padding: "8px 20px",
      borderRadius: 8,
    },
    [theme.breakpoints.up("xl")]: {
      fontSize: "1.35rem",
      padding: "10px 24px",
      borderRadius: 8,
    },
  },
  landscapeStatusWrap: {
    flexShrink: 0,
    marginLeft: theme.spacing(1.5),
    alignSelf: "center",
  },
  landscapeTitleRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
    marginBottom: "0.5rem",
  },
  landscapeMetaRow: {
    display: "flex",
    justifyContent: "space-between",
    flexBasis: "100%",
    alignItems: "center",
    borderTop: "1px solid #eee",
    paddingTop: "0.5rem",
  },
  title: {
    flex: 1,
    textAlign: "left",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",

    [theme.breakpoints.down("sm")]: {
      flexBasis: "100%",
      fontSize: "1.1rem",
      marginBottom: theme.spacing(0.5),
    },

    [theme.breakpoints.up("md")]: {
      textAlign: "center",
      fontWeight: "bold",
      fontSize: "1.4rem",
      marginBottom: 15,
    },
    [theme.breakpoints.up("lg")]: {
      fontSize: "1.35em",
    },
    [theme.breakpoints.up("xl")]: {
      fontSize: "2em",
    },
  },
  header: {
    flex: 1,
    textAlign: "left",
    fontSize: "0.9rem",
    margin: 0,
    [theme.breakpoints.up("sm")]: {
      fontSize: "1.1rem",
    },
  },
  status: {
    color: "#33a136",
    fontWeight: "bold",
  },
  titleRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
    marginBottom: theme.spacing(1),
  },
  statusBadge: {
    backgroundColor: "#33a136",
    color: "#fff",
    padding: "2px 8px",
    borderRadius: "4px",
    fontSize: "0.7rem",
    fontWeight: "bold",
    textTransform: "uppercase",
    marginLeft: theme.spacing(1),
  },
  actionRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
  },
  toggleButton: {
    marginLeft: theme.spacing(1),
    fontSize: "0.75rem",
    minWidth: "120px",
    lineHeight: 1.2,
    [theme.breakpoints.up("sm")]: {
      fontSize: "0.875rem",
    },
  },
  headerActions: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    flexShrink: 0,
  },
}));
